/**
 * Synthesized sound effects (Web Audio — no audio files).
 *
 * The AudioContext is created lazily on the first user gesture (browsers block
 * audio before one) and every effect is a few oscillators / filtered noise
 * bursts with short envelopes through a quiet master gain (~0.18). Nothing
 * ever plays before a gesture or while sound is toggled off.
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuffer: AudioBuffer | null = null;
let enabled = true;

/** Mirror of the UI sound toggle. */
export function setSoundEnabled(on: boolean): void {
  enabled = on;
}

function createContext(): void {
  if (ctx) return;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return;
  try {
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.18;
    // A gentle compressor keeps stacked effects (bingo + score) from clipping.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.ratio.value = 4;
    master.connect(comp).connect(ctx.destination);
    const length = Math.floor(ctx.sampleRate * 0.6);
    noiseBuffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  } catch {
    ctx = null;
  }
}

function unlock(): void {
  createContext();
  if (ctx && ctx.state === 'suspended') void ctx.resume();
}

/** Install once: the first pointer/key gesture creates/resumes the context. */
export function installAudioUnlock(): () => void {
  const opts = { capture: true, passive: true } as const;
  window.addEventListener('pointerdown', unlock, opts);
  window.addEventListener('keydown', unlock, opts);
  return () => {
    window.removeEventListener('pointerdown', unlock, opts);
    window.removeEventListener('keydown', unlock, opts);
  };
}

function ready(): AudioContext | null {
  if (!enabled || !ctx || !master || ctx.state !== 'running') return null;
  return ctx;
}

interface ToneOpts {
  freq: number;
  to?: number;
  type?: OscillatorType;
  at?: number;
  dur?: number;
  gain?: number;
  attack?: number;
  detune?: number;
}

function tone(ac: AudioContext, o: ToneOpts): void {
  const t0 = ac.currentTime + (o.at ?? 0);
  const dur = o.dur ?? 0.18;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  if (o.detune) osc.detune.value = o.detune;
  const peak = o.gain ?? 0.6;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + (o.attack ?? 0.008));
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(master!);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

interface NoiseOpts {
  at?: number;
  dur?: number;
  gain?: number;
  freq?: number;
  q?: number;
  type?: BiquadFilterType;
}

function noise(ac: AudioContext, o: NoiseOpts): void {
  if (!noiseBuffer) return;
  const t0 = ac.currentTime + (o.at ?? 0);
  const dur = o.dur ?? 0.06;
  const src = ac.createBufferSource();
  src.buffer = noiseBuffer;
  const filter = ac.createBiquadFilter();
  filter.type = o.type ?? 'bandpass';
  filter.frequency.value = o.freq ?? 1800;
  filter.Q.value = o.q ?? 1.2;
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(o.gain ?? 0.7, t0 + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter).connect(g).connect(master!);
  src.start(t0, Math.random() * 0.4);
  src.stop(t0 + dur + 0.02);
}

const NOTE = (semitonesFromA4: number) => 440 * Math.pow(2, semitonesFromA4 / 12);

export const sfx = {
  /** Soft click when a tile is picked up / selected. */
  pick() {
    const ac = ready();
    if (!ac) return;
    noise(ac, { dur: 0.035, gain: 0.35, freq: 3200, q: 2 });
    tone(ac, { freq: 1250, to: 900, dur: 0.05, gain: 0.12, type: 'triangle' });
  },
  /** Wooden "tock": a filtered noise burst plus a low sine. */
  place() {
    const ac = ready();
    if (!ac) return;
    noise(ac, { dur: 0.05, gain: 0.55, freq: 900, q: 3 });
    tone(ac, { freq: 220, to: 150, dur: 0.12, gain: 0.5 });
    tone(ac, { freq: 660, to: 520, dur: 0.05, gain: 0.12, type: 'triangle' });
  },
  /** Reverse tick when a tile goes back to the rack. */
  return() {
    const ac = ready();
    if (!ac) return;
    tone(ac, { freq: 520, to: 880, dur: 0.07, gain: 0.18, type: 'triangle' });
    noise(ac, { at: 0.03, dur: 0.03, gain: 0.2, freq: 2600, q: 2 });
  },
  /** Rapid ticks (tiles clattering). */
  shuffle() {
    const ac = ready();
    if (!ac) return;
    for (let i = 0; i < 6; i++) {
      noise(ac, { at: i * 0.045 + Math.random() * 0.01, dur: 0.03, gain: 0.3, freq: 1400 + Math.random() * 1600, q: 2.5 });
    }
  },
  /** Low detuned blip. */
  invalid() {
    const ac = ready();
    if (!ac) return;
    tone(ac, { freq: 196, dur: 0.16, gain: 0.35, type: 'square', detune: -12 });
    tone(ac, { freq: 185, at: 0.07, dur: 0.18, gain: 0.3, type: 'square', detune: 14 });
  },
  /** Warm two-note chime. */
  submit() {
    const ac = ready();
    if (!ac) return;
    tone(ac, { freq: NOTE(3), dur: 0.35, gain: 0.35, type: 'triangle' });
    tone(ac, { freq: NOTE(10), at: 0.09, dur: 0.45, gain: 0.3, type: 'triangle' });
  },
  /** Ascending arpeggio; more notes for bigger scores. */
  score(points: number) {
    const ac = ready();
    if (!ac) return;
    const steps = [0, 4, 7, 12, 16, 19];
    const count = Math.max(2, Math.min(steps.length, 2 + Math.floor(points / 12)));
    steps.slice(0, count).forEach((s, i) => {
      tone(ac, { freq: NOTE(s + 3), at: 0.12 + i * 0.07, dur: 0.28, gain: 0.26, type: 'sine' });
    });
  },
  /** Sparkly arpeggio for a bingo. */
  bingo() {
    const ac = ready();
    if (!ac) return;
    [0, 4, 7, 11, 12, 16, 19, 24].forEach((s, i) => {
      tone(ac, { freq: NOTE(s + 8), at: 0.2 + i * 0.055, dur: 0.4, gain: 0.2, type: 'triangle' });
      tone(ac, { freq: NOTE(s + 20), at: 0.22 + i * 0.055, dur: 0.25, gain: 0.06, type: 'sine' });
    });
  },
  /** Gentle ding when it becomes your turn. */
  turn() {
    const ac = ready();
    if (!ac) return;
    tone(ac, { freq: NOTE(7), dur: 0.6, gain: 0.28, type: 'sine' });
    tone(ac, { freq: NOTE(19), dur: 0.4, gain: 0.06, type: 'sine' });
  },
  /** Timer tick (last five seconds). */
  tick() {
    const ac = ready();
    if (!ac) return;
    tone(ac, { freq: 1600, dur: 0.03, gain: 0.12, type: 'square' });
  },
  /** Short cadence at the end of a game. */
  gameOver(won: boolean) {
    const ac = ready();
    if (!ac) return;
    const seq = won ? [0, 4, 7, 12] : [7, 3, 0, -5];
    seq.forEach((s, i) => tone(ac, { freq: NOTE(s), at: i * 0.14, dur: 0.5, gain: 0.26, type: 'triangle' }));
  },
};
